import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dah9b1jp14a1h3mr6g2g";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }
    formData.delete("website_alt");

    setStatus("loading");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") {
          params.append(key, value);
        }
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = String(
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "",
      );
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      className="rounded-lg border border-background-200 bg-background-100/70 p-5 md:p-7"
    >
      <div className="flex flex-col gap-1.5">
        <h3 className="font-heading text-xl font-bold text-foreground-950">
          订阅每日速递 / 投递线索
        </h3>
        <p className="text-sm leading-relaxed text-foreground-600">
          留下邮箱，我们会在早上八点把当日清单发给你；如果你手上有可核对的线索，也可以直接写在这里。
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="subscriber-email" className="text-sm font-medium text-foreground-800">
            你的邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="subscriber-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            autoComplete="email"
            className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="subscriber-topic" className="text-sm font-medium text-foreground-800">
            提交类型
          </label>
          <select
            id="subscriber-topic"
            name="topic"
            defaultValue="订阅每日速递"
            className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="订阅每日速递">订阅每日速递</option>
            <option value="线索投递">线索投递</option>
            <option value="内容纠错">内容纠错</option>
            <option value="合作与建议">合作与建议</option>
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="subscriber-detail" className="text-sm font-medium text-foreground-800">
          补充说明（选填，最多 500 字）
        </label>
        <textarea
          id="subscriber-detail"
          name="detail"
          maxLength={500}
          rows={4}
          placeholder="例如：某条时间线缺少哪一段记录、线索发生的大致时间与可核对的依据..."
          className="resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <div className="field-aux-note">
        <label htmlFor="subscriber-website-alt">备用站点地址</label>
        <input
          id="subscriber-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          {status === "loading" ? "正在提交" : "提交"}
        </button>
        <p className="text-xs leading-relaxed text-foreground-500">
          我们只会用它发送速递清单，不会对外提供你的邮箱。
        </p>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5" />
          提交成功，感谢你的支持。若有可核对的新线索，我们会在核实后于快报专栏更新。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {formError}
        </p>
      )}
    </form>
  );
}